import React from 'react';

import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import type { SupportedTimeZone } from '@/constants/date';
import { dateToNaiveString, getTodayDateInTimezone, naiveStringToDate } from '@/utils/date';

import { CallDirectionFilter } from './CallDirectionFilter';

interface FilterOption {
  label: string;
  value: string;
}

interface AnalyticsFiltersProps {
  programOptions: FilterOption[];
  selectedProgramIds: string[];
  onProgramFilterChange: (next: string[]) => void;

  /** Omit both to hide the call-direction filter (e.g. the Agents tab, AE/PQC). */
  selectedCallDirections?: string[];
  onCallDirectionsChange?: (next: string[]) => void;

  start: string;
  end: string;
  timeZone: SupportedTimeZone;
  onDateRangeChange: (range: { start: string; end: string }) => void;

  /** Show the inline date-range picker. Defaults to true. */
  showDateRange?: boolean;

  /** Optional slot for tab-specific extra filters between Program and Date. */
  extraFilters?: React.ReactNode;
  /** Optional slot rendered after the date picker (e.g. the Advanced-filters toggle). */
  trailingFilters?: React.ReactNode;
}

export const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({
  programOptions,
  selectedProgramIds,
  onProgramFilterChange,
  selectedCallDirections = [],
  onCallDirectionsChange,
  start,
  end,
  timeZone,
  onDateRangeChange,
  showDateRange = true,
  extraFilters,
  trailingFilters,
}) => {
  const today = getTodayDateInTimezone(timeZone);
  const initialFrom = naiveStringToDate(start || '') ?? today;
  const initialTo = naiveStringToDate(end || '') ?? today;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <SearchableMultiSelect
        options={programOptions}
        selected={selectedProgramIds}
        onChange={onProgramFilterChange}
        placeholder="All Programs"
        className="w-44 px-3"
        contentClassName="w-auto min-w-[220px]"
        searchPlaceholder="Search programs..."
        emptyMessage="No programs found."
      />

      {onCallDirectionsChange && (
        <CallDirectionFilter
          selected={selectedCallDirections ?? []}
          onChange={onCallDirectionsChange}
        />
      )}

      {extraFilters}

      {showDateRange && (
        <DateRangePicker
          // key forces a remount when URL params change externally (e.g. preset clicks
          // from chips), so the picker reflects the new range without manual sync.
          key={`${start}-${end}`}
          initialDateFrom={initialFrom}
          initialDateTo={initialTo}
          timezone={timeZone}
          showCompare={false}
          onUpdate={({ range }) => {
            if (!range.from) return;
            onDateRangeChange({
              start: dateToNaiveString(range.from),
              end: dateToNaiveString(range.to || range.from),
            });
          }}
        />
      )}

      {trailingFilters}
    </div>
  );
};
